import React, { useMemo } from 'react';
import {
  getColorSwatchSegments,
  lightenHexColor,
  normalizeColorToCanonical
} from '../../utils/colorUtils';
import MultipolarCableIcon from './MultipolarCableIcon';
import UnipolarCableIcon from './UnipolarCableIcon';

const STOCK_MULTIPOLAR_POLE_COLORS = ['RED', 'BLUE', 'YE/GN'];

const CableTypeIcon = ({ isMultipolar = false, color = '', jacketColor = '', poleColors = [], poleCount = 0, className = '' }) => {
  const unipolarStyle = useMemo(() => {
    const segments = getColorSwatchSegments(color);
    const primary = segments[0] || '#808080';
    const secondary = segments[1] || primary;
    const sectionPrimary = lightenHexColor(primary);
    const sectionSecondary = lightenHexColor(secondary);

    return {
      '--cable-jacket-primary': primary,
      '--cable-jacket-secondary': secondary,
      '--cable-jacket-section-primary': sectionPrimary,
      '--cable-jacket-section-secondary': sectionSecondary
    };
  }, [color]);

  const multipolarJacketStyle = useMemo(() => {
    if (!String(jacketColor || '').trim()) return {};

    const primary = getColorSwatchSegments(jacketColor)[0] || '#404040';
    return {
      '--multipolar-jacket-color': primary,
      '--multipolar-jacket-section-color': lightenHexColor(primary)
    };
  }, [jacketColor]);

  if (isMultipolar) {
    const normalizedPoleCount = Number.parseInt(poleCount, 10) || poleColors.length;
    const configuredPoleColors = poleColors.slice(0, normalizedPoleCount);
    const hasCompletePoleColors = configuredPoleColors.length === normalizedPoleCount &&
      configuredPoleColors.every((poleColor) => String(poleColor || '').trim());
    const earthColor = configuredPoleColors.find(
      (poleColor) => normalizeColorToCanonical(poleColor) === 'YE/GN'
    );
    const dynamicPoleColors = normalizedPoleCount > 3
      ? [configuredPoleColors[0], configuredPoleColors[1], earthColor || configuredPoleColors[2]]
      : configuredPoleColors;
    const visiblePoleCount = normalizedPoleCount === 2 ? 2 : 3;
    const canRenderDynamic = normalizedPoleCount >= 2 &&
      hasCompletePoleColors &&
      dynamicPoleColors.length === visiblePoleCount &&
      dynamicPoleColors.every((poleColor) => String(poleColor || '').trim());

    if (canRenderDynamic) {
      return (
        <MultipolarCableIcon
          poleColors={dynamicPoleColors}
          poleCount={visiblePoleCount}
          className={className}
          style={multipolarJacketStyle}
        />
      );
    }

    return (
      <MultipolarCableIcon
        poleColors={STOCK_MULTIPOLAR_POLE_COLORS}
        poleCount={3}
        stock
        className={className}
        style={multipolarJacketStyle}
      />
    );
  }

  return <UnipolarCableIcon className={className} style={unipolarStyle} />;
};

export default CableTypeIcon;
